import React, {useEffect, useState} from 'react';
import {Flex, Form, Input, message, Radio, Select, Upload} from "antd";
import {useDispatch} from "react-redux";
import classes from "../../Applications/application.module.css";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import {useTranslation} from "react-i18next";
import UserService from "../../../../../services/UserService";
import CertificateService from "../../../../../services/CertificateService";
import GroupService from "../../../../../services/GroupService";
import {LocalName} from "../../../../../utils/LocalName";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import {clrs} from "../../../../../constants/colors";
import {InboxOutlined} from "@ant-design/icons";
import HorizontalDivider from "../../../../../components/Divider/HorizontalDivider";
import FileUploaderService from "../../../../../services/FileUploaderService";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";

const ALL_VALUE = 'ALL';
const {Dragger} = Upload;

const CertificateCreate = (fetchStudents1: *) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const [students, setStudents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [studentsWithAll, setStudentsWithAll] = useState([]);
    const [currentGroupStudents, setCurrentGroupStudents] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [isLoadingCreate, setIsLoadingCreate] = useState(false);

    const [uploadedFile, setUploadedFile] = useState(null);

    const method = Form.useWatch('method', form);
    const template = Form.useWatch('template', form);
    const student = Form.useWatch('student', form);
    const code = Form.useWatch('code', form);
    const group = Form.useWatch('group', form);
    const selectedStudents = Form.useWatch('students', form);

    const options = [
        {label: <IntlMessage id={"certificate.byJournal"}/>, value: 'group'},
        {label: <IntlMessage id={"certificate.manualGeneration"}/>, value: 'student'},
        {label: <IntlMessage id={"certificate.fromExcel"}/>, value: 'excel'},
    ];

    const [fields, setCustomFields] = useState([]);

    useEffect(() => {
        const selected = templates.find(t => t.value === form.getFieldValue("template"));
        setCustomFields(selected?.codeNames || []);
    }, [form.getFieldValue("template"), templates]);

    useEffect(() => {
        fetchTemplates();
    }, []);

    useEffect(() => {
        if (method === 'student') {
            fetchStudents();
        }
        if (method === 'group') {
            fetchGroups();
        }
    }, [method]);

    useEffect(() => {
        const allStudentIds = currentGroupStudents.map(student => student.value);

        const isAllSelected = allStudentIds.length > 0 && selectedStudents?.length === allStudentIds.length && allStudentIds.every(id => selectedStudents.includes(id));

        if (currentGroupStudents.length > 0) {
            const newOptions = isAllSelected
                ? [...currentGroupStudents]
                : [{value: ALL_VALUE, label: 'Все'}, ...currentGroupStudents];

            setStudentsWithAll(newOptions);
        } else {
            setStudentsWithAll([]);
        }
    }, [selectedStudents, currentGroupStudents]);

    const fetchStudents = async () => {
        await UserService.getStudents(1, 100)
            .then((r) => {
                const fetchedStudents = r.data.map(student => ({
                    value: student.id,
                    label: student.fullName,
                }));
                setStudents(fetchedStudents)
                setStudentsWithAll([
                    {value: ALL_VALUE, label: 'Все'},
                    ...fetchedStudents,
                ]);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id="error"/>, 5);
            })
            .finally(() => {

            })
    }

    const fetchGroups = async () => {
        await GroupService.getGroupsForCertificateCreate(1, 1000)
            .then((r) => {
                const sortedGroups = r.data
                    .map(group => ({
                        value: group.id,
                        label: LocalName.getName(group) + " " + group.number,
                        students: group.students || [],
                        nameRu: group.nameRu,
                        number: group.number,
                    }))
                    .filter(group => {
                        const name = group.nameRu?.toLowerCase();
                        const number = Number(group.number);

                        if (name === 'intermediate' && [1,2,3,4,5,6,7,8].includes(number)) return false;
                        if (name === 'pre-intermediate' && number >= 1 && number <= 15) return false;
                        return true;
                    })
                    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))

                setGroups(sortedGroups);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id="error"/>, 5);
            })
            .finally(() => {

            })
    }

    const fetchTemplates = async () => {
        await CertificateService.getAllCertificateTemplates(1, 100)
            .then((r) => {
                setTemplates(r.data.map(t => ({
                        value: t.id,
                        label: t.name,
                        codeNames: t.codeNames || []  // сохраняем codeNames
                    }))
                );
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id="error"/>, 5);
            });
    }

    const handleGroupChange = (groupId) => {
        form.setFieldsValue({students: []}); // Очищаем студентов
        setSelectedGroupId(groupId);

        const selectedGroup = groups.find(group => group.value === groupId);

        if (selectedGroup && selectedGroup.students.length > 0) {
            const formattedStudents = selectedGroup.students.map(student => ({
                value: student.id,
                label: `${student.profile.firstName} ${student.profile.lastName}`,
            }));

            setCurrentGroupStudents(formattedStudents);
        } else {
            setCurrentGroupStudents([]);
        }
    };

    const handleStudentsChange = (value) => {
        const allStudentIds = currentGroupStudents.map(student => student.value);

        if (value.includes(ALL_VALUE)) {
            form.setFieldsValue({students: allStudentIds});
        } else {
            const isAllSelected = allStudentIds.length > 0 && allStudentIds.every(id => value.includes(id));

            if (isAllSelected && value.length === allStudentIds.length) {
                form.setFieldsValue({students: allStudentIds});
            } else {
                form.setFieldsValue({students: value});
            }
        }
    };

    function handleCreate() {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        form.validateFields()
            .then(async (values) => {
                setIsLoadingCreate(true);

                if (values.method === 'group') {
                    await CertificateService.saveCertificateGroup(template, group)
                        .then(() => {
                            message.success(<IntlMessage id={'success'}/>)
                            form.resetFields();
                        })
                        .catch()
                        .finally()
                } else if (values.method === 'student') {
                    await CertificateService.saveCertificateManual(template, values.student, code, values.fields)
                        .then(() => {
                            message.success(<IntlMessage id={'success'}/>)
                            form.resetFields();
                        })
                        .catch()
                        .finally()
                } else if (values.method === 'excel') {
                    if (!uploadedFile) {
                        message.error(<IntlMessage id="file.required" defaultMessage="Please upload a file"/>, 5);
                        return;
                    }
                    try {
                        await CertificateService.convertExcelToCertificates(uploadedFile, template);
                        message.success(<IntlMessage id="success"/>, 5);
                        form.resetFields();
                        setUploadedFile(null); // Очистить файл
                    } catch (error) {
                        message.error(<IntlMessage id="error"/>, 5);
                    }
                }

            })
            .catch((error) => {
                console.log("Validation error!", error);
            })
            .finally(() => {
                setIsLoadingCreate(false);
                message.destroy();
            });
    }

    const getValidationRules = (field, methodField = 'method') => {
        return [
            ({getFieldValue}) => ({
                validator(_, value) {
                    const method = getFieldValue(methodField);

                    if (field === 'group' && method === 'group' && !value) {
                        return Promise.reject(<IntlMessage id="requiredField"/>);
                    }
                    if (field === 'students' && method === 'group' && (!value || value.length === 0)) {
                        return Promise.reject(<IntlMessage id="requiredField"/>);
                    }
                    if (field === 'student' && method === 'student' && !value) {
                        return Promise.reject(<IntlMessage id="requiredField"/>);
                    }
                    return Promise.resolve();
                }
            })
        ];
    };

    const handleChangeExcel = (info) => {
        // Сохраняем выбранный файл в state
        setUploadedFile(info.file);
        // Никакой загрузки на сервер пока
    };

    const propsOtherFiles = {
        beforeUpload: () => false,
        onChange: handleChangeExcel,
        multiple: false,
    };


    return (
        <Form form={form} className={classes.form} initialValues={{method: 'group'}}>
            <BlockLoading isLoading={isLoadingCreate}/>
            <Flex vertical style={{width: '100%'}} gap={10}>
                <Form.Item name="method">
                    <Radio.Group
                        options={options}
                        optionType="button"
                        buttonStyle="solid"
                        defaultValue="group"
                    />
                </Form.Item>

                <Form.Item
                    name="template"
                    label={<IntlMessage id="certificate.template"/>}
                    labelCol={{span: 24}}
                    className={classes.formInputBlock}
                    rules={[{required: true, message: <IntlMessage id="requiredField"/>}]}
                >
                    <Select
                        allowClear
                        options={templates}
                        placeholder={<IntlMessage id="certificate.template"/>}
                    />
                </Form.Item>

                {method === 'group' && (
                    <>
                        <Form.Item
                            name="group"
                            label={<IntlMessage id="group"/>}
                            labelCol={{span: 24}}
                            className={classes.formInputBlock}
                            dependencies={['method']}
                            rules={getValidationRules('group')}
                        >
                            <Select
                                allowClear
                                options={groups}
                                placeholder={<IntlMessage id="group"/>}
                                onChange={handleGroupChange}
                            />
                        </Form.Item>

                        {/*<Form.Item*/}
                        {/*    name="students"*/}
                        {/*    label={<IntlMessage id="ordersPage.students"/>}*/}
                        {/*    labelCol={{span: 24}}*/}
                        {/*    className={classes.formInputBlock}*/}
                        {/*    dependencies={['method']}*/}
                        {/*    rules={getValidationRules('students')}*/}
                        {/*>*/}
                        {/*    <Select*/}
                        {/*        mode="multiple"*/}
                        {/*        allowClear*/}
                        {/*        options={studentsWithAll}*/}
                        {/*        placeholder={<IntlMessage id="ordersPage.students"/>}*/}
                        {/*        onChange={handleStudentsChange}*/}
                        {/*        disabled={!selectedGroupId}*/}
                        {/*    />*/}
                        {/*</Form.Item>*/}
                    </>
                )}

                {method === 'student' && fields.length > 0 && (
                    <>
                        <Form.Item
                            name="student"
                            label={<IntlMessage id="student"/>}
                            labelCol={{span: 24}}
                            className={classes.formInputBlock}
                            dependencies={['method']}
                            // rules={getValidationRules('student')}
                        >
                            <Select
                                allowClear
                                options={students}
                                placeholder={<IntlMessage id="ordersPage.students"/>}
                            />
                        </Form.Item>
                        <Form.Item
                            name="code"
                            label={<IntlMessage id="certCode"/>}
                            labelCol={{span: 24}}
                            className={classes.formInputBlock}
                            rules={getValidationRules('student')}
                        >
                            <Input placeholder={t("certCode")}/>
                        </Form.Item>
                        {fields.map(field => (
                            <Form.Item
                                key={field.code}
                                name={['fields', field.code]} // создаст структуру fields: { code: value }
                                label={field.name}
                                labelCol={{span: 24}}
                                className={classes.formInputBlock}
                                rules={[{required: true, message: <IntlMessage id="requiredField"/>}]}
                            >
                                <Input placeholder={field.name}/>
                            </Form.Item>
                        ))}
                    </>
                )}

                {method === 'excel' && (
                    <Flex align={"flex-start"} vertical justify={"flex-start"} gap={10} style={{width: "100%"}}>
                        {/*<Upload {...propsOtherFiles} fileList={[]}>*/}
                        {/*    <AntdButton icon={<UploadOutlined/>}><IntlMessage id={'upload'}/></AntdButton>*/}
                        {/*</Upload>*/}
                        <div style={{width: "100%"}}>
                            <Dragger {...propsOtherFiles} fileList={uploadedFile ? [uploadedFile] : []}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text"><IntlMessage id={"certificate.dragFileExcel"}/></p>
                                <p className="ant-upload-hint">
                                    <IntlMessage id={"certificate.dragFileExcel2"}/>
                                </p>
                            </Dragger>

                        </div>

                        <HorizontalDivider/>
                        {/*<FileList files={getForm.form?.otherFiles} setIsLoading={setIsLoadingOtherFiles}/>*/}
                    </Flex>
                )}


                <GreyButton
                    onClick={handleCreate}
                    backgroundColor={clrs.blackerBlue}
                    fontColor={clrs.white}
                >
                    <IntlMessage id="create"/>
                </GreyButton>
            </Flex>
        </Form>
    );


};

export default CertificateCreate;